/*
 * Copyright (c) 2019. World Wide Generation Ltd
 */

import { UtrValueTypeMap, UtrValueTypes, isNumeric, getName, getTypeCode } from '@utils/universalTracker';
import { UnitTypes } from '@utils/units';
import { standards, frameworks } from '@g17eco/core';
import {
  AlternativeProps,
  Tags,
  UniversalTrackerBase,
  UniversalTrackerPlain,
  UniversalTrackerType,
  UtrValueType,
  ValueValidation,
} from '../types/universalTracker';
import { SerializedEditorState } from 'lexical';
import {
  InitiativeUniversalTracker,
  MergedValueListOption,
  ValueListConfig,
} from '@g17eco/types/initiativeUniversalTracker';
import UniversalTrackerInterface from './UniversalTrackerClassInterface';
import { overrideValueList } from '@utils/initiative-universal-tracker';

/**
 * Should no longer use this class and prefer plain UTR with utility functions
 */
export default class UniversalTracker implements UniversalTrackerInterface {
  constructor(
    private universalTracker: UniversalTrackerBase,
    private type: UniversalTrackerType = UniversalTrackerType.Utr,
  ) {
    this.universalTracker = universalTracker;
    this.type = type;
  }

  private mergeTableValidation(
    valueValidation: ValueValidation,
    initiativeUtrValueValidation: ValueValidation | undefined,
  ) {
    if (!initiativeUtrValueValidation?.table || !valueValidation.table) {
      return {};
    }

    const mergedColumns = valueValidation.table.columns.map((col) => {
      const overriddenColumn = initiativeUtrValueValidation.table?.columns.find((c) => c.code === col.code);
      const newColumn = { ...col };
      if (overriddenColumn?.unitInput) {
        newColumn.unitInput = overriddenColumn.unitInput;
      }
      if (overriddenColumn?.numberScaleInput) {
        newColumn.numberScaleInput = overriddenColumn.numberScaleInput;
        newColumn.numberScale = col.numberScale ?? 'single';
      }
      if (overriddenColumn?.validation?.decimal !== undefined) {
        newColumn.validation = { ...col.validation, decimal: overriddenColumn.validation.decimal };
      }
      return newColumn;
    });

    return { table: { ...valueValidation.table, columns: mergedColumns } };
  }

  public overrideValueValidation(initiativeUtrValueValidation: ValueValidation | undefined) {
    const valueValidation = this.getValueValidation();
    const tableValueValidation = this.mergeTableValidation(valueValidation, initiativeUtrValueValidation);
    this.universalTracker = {
      ...this.universalTracker,
      valueValidation: {
        ...valueValidation,
        ...initiativeUtrValueValidation,
        ...tableValueValidation,
      },
    };
  }

  private overrideValueList(valueListConfig: ValueListConfig<MergedValueListOption> | undefined) {
    this.universalTracker = {
      ...this.universalTracker,
      valueValidation: overrideValueList({ valueValidation: this.universalTracker.valueValidation, valueListConfig }),
    };
  }

  private overrideUnitAndNumberScaleInput(
    initiativeUtr: Pick<InitiativeUniversalTracker, 'unitInput' | 'numberScaleInput'>,
  ) {
    const overrides: Pick<UniversalTrackerPlain, 'unitInput' | 'numberScaleInput' | 'numberScale'> = {};
    if (initiativeUtr.unitInput) {
      overrides.unitInput = initiativeUtr.unitInput;
    }
    if (initiativeUtr.numberScaleInput) {
      overrides.numberScaleInput = initiativeUtr.numberScaleInput;
      overrides.numberScale = this.universalTracker.numberScale ?? 'single';
    }
    if (Object.keys(overrides).length > 0) {
      this.universalTracker = {
        ...this.universalTracker,
        ...overrides,
      };
    }
  }

  public setMetricOverrides(
    initiativeUtr: Pick<
      InitiativeUniversalTracker<MergedValueListOption>,
      'valueValidation' | 'unitInput' | 'numberScaleInput' | 'valueListConfig'
    >,
  ) {
    this.overrideUnitAndNumberScaleInput(initiativeUtr);
    this.overrideValueValidation(initiativeUtr.valueValidation);
    this.overrideValueList(initiativeUtr.valueListConfig);
  }

  public getRaw<T extends UniversalTrackerBase = UniversalTrackerPlain>(): T {
    return this.universalTracker as T;
  }

  public getId() {
    return this.universalTracker._id;
  }

  public getCode() {
    return this.universalTracker.code;
  }

  private getAlternativeWithFallback(
    attribute: keyof AlternativeProps,
    alternativeCode: string,
    secondaryFallback: string,
  ): string;

  private getAlternativeWithFallback(
    attribute: 'instructionsEditorState',
    alternativeCode: string,
    secondaryFallback: undefined,
  ): SerializedEditorState | undefined;

  private getAlternativeWithFallback(
    attribute: keyof AlternativeProps | 'instructionsEditorState',
    alternativeCode: string,
    secondaryFallback: string | undefined,
  ): string | SerializedEditorState | undefined {
    if (attribute === 'instructionsEditorState') {
      return alternativeCode === this.getType()
        ? this.universalTracker[attribute]
        : this.universalTracker.alternatives?.[alternativeCode]?.[attribute];
    }
    if (alternativeCode === this.getType()) {
      return this.universalTracker[attribute] || secondaryFallback;
    }
    if (alternativeCode && this.hasAlternativeInfo(alternativeCode) && 'alternatives' in this.universalTracker) {
      return this.universalTracker.alternatives?.[alternativeCode]?.[attribute] || secondaryFallback;
    }
    return this.universalTracker[attribute] || secondaryFallback;
  }

  public getShortPrefix(alternativeCode = '') {
    const type = standards[alternativeCode]?.shortName || '';
    const code = this.getTypeCode(alternativeCode);
    if (type) {
      return `${type} ${code}`;
    }
    return code;
  }

  public getName(alternativeCode = '') {
    return this.getAlternativeWithFallback('name', alternativeCode, '');
  }

  public getBestName(alternativeCodes?: string[]): string {
    return getName(this.universalTracker as UniversalTrackerPlain, alternativeCodes);
  }

  public getBestTypeCode(alternativeCodes?: string[]): string {
    return getTypeCode(this.universalTracker as UniversalTrackerPlain, alternativeCodes);
  }

  public getValueLabel(alternativeCode = '') {
    return this.getAlternativeWithFallback('valueLabel', alternativeCode, 'Value');
  }

  public getInstructions(alternativeCode = '') {
    return this.getAlternativeWithFallback('instructions', alternativeCode, '');
  }

  public getInstructionsEditorState(alternativeCode = '') {
    return this.getAlternativeWithFallback('instructionsEditorState', alternativeCode, undefined);
  }

  public getEvidenceInstructions(alternativeCode = '') {
    return this.getAlternativeWithFallback('evidenceInstructions', alternativeCode, '');
  }

  public getInstructionsLink(alternativeCode = '') {
    return this.getAlternativeWithFallback('instructionsLink', alternativeCode, '');
  }

  public hasAlternativeInfo(alternativeCode = '') {
    if (alternativeCode === this.getType()) {
      // Root level data should be the correct one, so no need to check alternatives
      return true;
    }

    return !!this.universalTracker.alternatives?.[alternativeCode];
  }

  public getAlternativeLanguages() {
    if (!this.universalTracker.alternatives) {
      return [];
    }

    const alternativeKeys = Object.keys(this.universalTracker.alternatives);
    return alternativeKeys.filter(
      (alternativeCode) => this.universalTracker.alternatives?.[alternativeCode].alternativeType === 'language',
    );
  }

  public getTargetDirection() {
    return this.universalTracker.targetDirection ?? '';
  }

  public getType() {
    return this.universalTracker.type;
  }

  public getTypeTags() {
    return this.universalTracker.typeTags ?? [];
  }

  public getAltTypeTags() {
    if (!this.universalTracker.alternatives) {
      return [];
    }

    const typeTags: string[] = [];
    Object.values(this.universalTracker.alternatives).forEach((alt) => {
      if (Array.isArray(alt.typeTags)) {
        typeTags.push(...alt.typeTags);
      }
    });
    return typeTags;
  }

  public getTypeName(alternativeCode = '') {
    const type = this.hasAlternativeInfo(alternativeCode) ? alternativeCode : this.getType();

    if (type === 'kpi') {
      return 'Standardized Metric';
    }
    if (type === 'custom_kpi') {
      return 'Custom Metric';
    }
    return standards[type]?.name || frameworks[type]?.name || '';
  }

  public getFrameworkCode() {
    return `${this.getType()}/${this.getTypeCode()}`;
  }

  public getTypeCode(alternativeCode = '') {
    return this.getAlternativeWithFallback('typeCode', alternativeCode, '');
  }

  public getSuffix() {
    return this.isPercentage() ? '%' : '';
  }

  public getMetricUnitCode() {
    return this.universalTracker.unit;
  }

  public getMetricUnitType() {
    return this.universalTracker.unitType;
  }

  public getNumberScale() {
    return this.universalTracker.numberScale;
  }

  public getNumberScaleInput() {
    return this.universalTracker.numberScaleInput;
  }

  public isCurrency() {
    return this.universalTracker.unitType === UnitTypes.currency;
  }

  public getValueType() {
    return this.universalTracker.valueType as UtrValueType;
  }

  public getValueTypeName() {
    return UtrValueTypeMap[this.universalTracker.valueType as UtrValueType] ?? '';
  }

  public isType(type: string) {
    return this.universalTracker.valueType === type;
  }

  public getValueListOptions() {
    if (
      ![
        UtrValueTypes.valueList,
        UtrValueTypes.valueListMulti,
        UtrValueTypes.numericValueList,
        UtrValueTypes.textValueList,
      ].includes(this.getValueType()) ||
      !this.universalTracker.valueValidation ||
      !this.universalTracker.valueValidation.valueList
    ) {
      return [];
    }

    let list: MergedValueListOption[] | undefined;
    const valueList = this.universalTracker.valueValidation.valueList;
    switch (valueList.type) {
      case 'list':
        list = valueList.list;
        break;
      case 'custom':
        list = valueList.custom;
        break;
      default:
        break;
    }

    if (Array.isArray(list)) {
      return list;
    }
    return [];
  }

  public getTags(group: keyof Tags) {
    return this.universalTracker.tags?.[group] ?? [];
  }
  public hasTag(group: string, tag: string) {
    return this.universalTracker.tags?.[group as keyof Tags]?.includes(tag);
  }
  public hasSubTag(tag?: string) {
    if (!this.universalTracker.tags) {
      return false;
    }
    // If its root tag, only include non-empty array,
    // as keys added by schema with default empty [] as value
    if (Number(this.universalTracker.tags[tag as keyof Tags]?.length) > 0) {
      return true;
    }
    return Object.values(this.universalTracker.tags).flat().includes(tag);
  }

  public hasMinAndMax() {
    if (this.getMin() === undefined || this.getMax() === undefined) {
      return false;
    }
    return true;
  }

  public getValueValidation() {
    if (!this.universalTracker.valueValidation) {
      return {};
    }

    return this.universalTracker.valueValidation;
  }

  public getMin(fallback = undefined) {
    if (!this.universalTracker.valueValidation) {
      return fallback;
    }
    if (typeof this.universalTracker.valueValidation.min !== 'number') {
      return fallback;
    }
    return this.universalTracker.valueValidation.min;
  }

  public getMax(fallback = undefined) {
    if (!this.universalTracker.valueValidation) {
      return fallback;
    }
    if (typeof this.universalTracker.valueValidation.max !== 'number') {
      return fallback;
    }
    return this.universalTracker.valueValidation.max;
  }

  public isPercentage() {
    return this.getValueType() === 'percentage';
  }

  public hasValueData() {
    switch (this.universalTracker.valueType) {
      case UtrValueType.Text:
      case UtrValueType.Date:
      case UtrValueType.ValueList:
      case UtrValueType.ValueListMulti:
      case UtrValueType.NumericValueList:
      case UtrValueType.TextValueList:
      case UtrValueType.Table:
        return true;
      case UtrValueType.Sample:
      case UtrValueType.Number:
      case UtrValueType.Percentage:
      default:
        return false;
    }
  }

  isNumeric() {
    return isNumeric(this.universalTracker);
  }
}
